import React, { useCallback, useRef, useState, useEffect } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { GoogleMap, Marker, InfoWindow, useLoadScript } from '@react-google-maps/api';
import { googleApiKey } from 'utils/config';
import { getVisibleOfferLocations, viewportFromMap } from 'utils/offerLocations';

const libraries = ['places'];
const initialLocalZoom = 11.4; // Approximately a 15-mile viewport; exact radius is measured on idle.

const mapContainerStyle = {
  width: '100%',
  height: '600px',
};

const defaultCenter = {
  lat: 39.661,
  lng: -98.048,
};

const defaultOptions = {
  isFractionalZoomEnabled: true,
  zoomControl: true,
  mapTypeControl: false,
  scaleControl: false,
  streetViewControl: false,
  rotateControl: false,
  fullscreenControl: false,
};

const MapView = ({
  offers = [],
  onMarkerClick,
  highlightedOfferId,
  userLocation,
  isVisible,
  viewportBounds,
  initialViewport,
  onViewportChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: googleApiKey,
    libraries,
  });

  const mapRef = useRef(null);
  const initialViewportRef = useRef(initialViewport);
  const initialLocationRef = useRef(`${userLocation?.latitude}:${userLocation?.longitude}`);
  const viewportTimerRef = useRef(null);
  const lastViewportRef = useRef(null);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom, setMapZoom] = useState(4);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    
    const saved = initialViewportRef.current;
    if (saved) {
      const latitudeDelta = saved.radius / (69 * Math.sqrt(2));
      const longitudeDelta = latitudeDelta / Math.max(0.01, Math.cos(saved.latitude * Math.PI / 180));
      map.fitBounds({
        north: Math.min(90, saved.latitude + latitudeDelta),
        south: Math.max(-90, saved.latitude - latitudeDelta),
        east: saved.longitude + longitudeDelta,
        west: saved.longitude - longitudeDelta,
      }, 0);
      setMapCenter({ lat: saved.latitude, lng: saved.longitude });
      return;
    }
    // Zoom to user location when map loads (this happens when component mounts/becomes visible)
    if (userLocation && userLocation.latitude && userLocation.longitude) {
      const userLatLng = {
        lat: parseFloat(userLocation.latitude),
        lng: parseFloat(userLocation.longitude)
      };
      
      map.setCenter(userLatLng);
      map.setZoom(initialLocalZoom);
      setMapCenter(userLatLng);
      setMapZoom(initialLocalZoom);
    }
  }, [userLocation]);

  // Zoom to user location when map becomes visible or user location changes
  // Since component is conditionally rendered, it remounts when becoming visible
  useEffect(() => {
    if (initialViewportRef.current
        && initialLocationRef.current === `${userLocation?.latitude}:${userLocation?.longitude}`) return;
    if (isVisible && userLocation && userLocation.latitude && userLocation.longitude && mapRef.current) {
      const userLatLng = {
        lat: parseFloat(userLocation.latitude),
        lng: parseFloat(userLocation.longitude)
      };

      // Small delay to ensure map is fully rendered
      const timeoutId = setTimeout(() => {
        if (mapRef.current) {
          mapRef.current.setCenter(userLatLng);
          mapRef.current.setZoom(initialLocalZoom);
          
          setMapCenter(userLatLng);
          setMapZoom(initialLocalZoom);
        }
      }, 200);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isVisible, userLocation]);

  const handleMarkerClick = (offer) => {
    setSelectedOffer(offer);
    if (onMarkerClick) {
      onMarkerClick(offer.id);
    }
  };

  const handleMapIdle = useCallback(() => {
    if (!mapRef.current) return;

    const viewport = viewportFromMap(mapRef.current);
    const center = mapRef.current.getCenter();
    if (center) {
      setMapCenter({ lat: center.lat(), lng: center.lng() });
      setMapZoom(mapRef.current.getZoom());
    }
    if (!viewport || !onViewportChange) return;

    const viewportKey = `${viewport.latitude}:${viewport.longitude}:${viewport.radius}`;
    if (viewportKey === lastViewportRef.current) return;
    lastViewportRef.current = viewportKey;

    clearTimeout(viewportTimerRef.current);
    viewportTimerRef.current = setTimeout(() => onViewportChange(viewport), 300);
  }, [onViewportChange]);

  useEffect(() => () => clearTimeout(viewportTimerRef.current), []);

  if (loadError) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        Error loading maps
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        Loading maps...
      </Box>
    );
  }

  // Helper function to offset coordinates slightly to avoid overlapping markers
  const offsetCoordinates = (coords, index, userLocation) => {
    if (!userLocation || !userLocation.latitude || !userLocation.longitude) {
      return coords;
    }

    const userLat = parseFloat(userLocation.latitude);
    const userLng = parseFloat(userLocation.longitude);
    const offerLat = coords.lat;
    const offerLng = coords.lng;

    // Check if coordinates are very close to user location (within ~0.001 degrees, roughly 100 meters)
    const latDiff = Math.abs(offerLat - userLat);
    const lngDiff = Math.abs(offerLng - userLng);
    const threshold = 0.001;

    if (latDiff < threshold && lngDiff < threshold) {
      // Offset by a small amount (approximately 50-100 meters) in a circular pattern
      // Use index to create a spiral pattern so multiple 0-mile offers don't overlap
      const angle = (index * 60) * (Math.PI / 180); // 60 degrees per offer
      const offsetDistance = 0.001; // Approximately 100 meters
      return {
        lat: offerLat + (offsetDistance * Math.cos(angle)),
        lng: offerLng + (offsetDistance * Math.sin(angle))
      };
    }

    return coords;
  };

  const offerMarkers = offers.flatMap((offer) =>
    getVisibleOfferLocations(offer, viewportBounds).map((coordinates) => ({
      ...offer,
      coordinates,
      markerKey: `${offer.id}:${coordinates.lat}:${coordinates.lng}`,
    })),
  ).map((offer, index) => ({
    ...offer,
    coordinates: offsetCoordinates(offer.coordinates, index, userLocation),
  }));

  // Dynamic map height based on screen size
  const mapHeight = isMobile ? '350px' : '600px';

  return (
    <Box sx={{ width: '100%', height: mapHeight, position: 'relative' }}>
      {/* Show message if no offers have coordinates */}
      {offers.length > 0 && offerMarkers.length === 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            left: 10,
            right: 10,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            padding: 2,
            borderRadius: 1,
            zIndex: 1000,
            textAlign: 'center',
          }}
        >
          <Box sx={{ fontWeight: 'bold', color: 'warning.main' }}>
            No offers with location data found
          </Box>
          <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
            {offers.length} offers available, but none have coordinates for map display
          </Box>
        </Box>
      )}
      
      <GoogleMap
        mapContainerStyle={{...mapContainerStyle, height: mapHeight}}
        zoom={mapZoom}
        center={mapCenter}
        options={defaultOptions}
        onLoad={onMapLoad}
        onIdle={handleMapIdle}
      >
        {/* User Location Marker */}
        {userLocation && userLocation.latitude && userLocation.longitude && (
          <Marker
            position={{
              lat: parseFloat(userLocation.latitude),
              lng: parseFloat(userLocation.longitude),
            }}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: '#4285f4',
              fillOpacity: 1,
              strokeColor: 'white',
              strokeWeight: 3,
            }}
            title="Your location"
          />
        )}

        {offerMarkers.map((offer, index) => (
          <Marker
            key={offer.markerKey}
            position={offer.coordinates}
            onClick={() => handleMarkerClick(offer)}
            label={{
              text: String(offer.displayIndex ?? index + 1),
              color: 'white',
              fontSize: '12px',
              fontWeight: 'bold',
            }}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: highlightedOfferId === offer.id ? 15 : 12,
              fillColor: highlightedOfferId === offer.id ? '#1976d2' : '#87CEEB', // Light blue for offers
              fillOpacity: 1,
              strokeColor: 'white',
              strokeWeight: 2,
            }}
          />
        ))}

        {selectedOffer && (
          <InfoWindow
            position={selectedOffer.coordinates}
            onCloseClick={() => setSelectedOffer(null)}
          >
            <Box sx={{ maxWidth: 200 }}>
              <Box sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {selectedOffer.headline1}
                {selectedOffer.headline2 && ` | ${selectedOffer.headline2}`}
              </Box>
              <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                {selectedOffer.distance || 'Distance unavailable'}
              </Box>
            </Box>
          </InfoWindow>
        )}
      </GoogleMap>
    </Box>
  );
};

export default MapView;
